/**
 * Apple iCloud Contacts — API client for the Address Book / Select Recipients modal.
 * Backend: sendlinxapi ICloudContactsController (api/user/contacts/icloud/...).
 *
 * Phase 1 uses CardDAV with an Apple app-specific password: the user types their Apple Account email
 * and an app-specific password, the server validates them with iCloud, stores the password encrypted
 * and imports the contacts read-only. The password is only sent once over HTTPS in the connect request;
 * it is never stored in the browser and never returned by the API.
 *
 * REACT_APP_ICLOUD_CONTACTS_MOCK=true (or REACT_APP_MS365_MOCK=true) runs the UI on sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const APPLE_ACCOUNT_URL = "https://account.apple.com/account/manage?utm_source=chatgpt.com";
export const APP_PASSWORD_PATTERN = /^[a-z]{4}-?[a-z]{4}-?[a-z]{4}-?[a-z]{4}$/i;

export const isICloudMockMode = () => {
    try{ return process.env.REACT_APP_ICLOUD_CONTACTS_MOCK === "true" || process.env.REACT_APP_MS365_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/* ---------------- mock store ---------------- */
const MOCK_PEOPLE = [
    ["Olivia Martin", "olivia.martin@icloud.example", "Martin & Co"], ["Ethan Brooks", "ethan@brooks.example", ""],
    ["Sofia Rossi", "sofia.rossi@me.example", "Rossi Studio"], ["", "accounts@vendor.example", "Vendor Inc"]
];
const mock = {
    status: { provider: "icloud", configured: true, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, addressBooks: 0, sync: null },
    rows: (() => {
        const rows = MOCK_PEOPLE.map(([name, email, company], i) => ({ id: "i"+i, source: "icloud", sourceId: "h:"+i, name,
            firstName: name.split(" ")[0] || "", lastName: name.split(" ").slice(1).join(" "), company, email, isPrimary: true, photoUrl: null }));
        for(let i = 1; i <= 60; i++){
            const n = String(i).padStart(2, "0");
            rows.push({ id: "is"+i, source: "icloud", sourceId: "h:s"+i, name: "iCloud Sample "+n, firstName: "iCloud", lastName: "Sample "+n,
                company: i % 5 === 0 ? "Example Inc" : "", email: "isample"+n+"@example.com", isPrimary: true, photoUrl: null });
        }
        return rows;
    })()
};
const copy = (v) => JSON.parse(JSON.stringify(v));
const delay = (v, ms = 250) => new Promise(resolve => setTimeout(() => resolve(copy(v)), ms));
const mockContacts = (query) => {
    const q = String(query.q || "").trim().toLowerCase();
    let rows = mock.rows.filter(r => !q || (r.name+" "+r.email+" "+r.company).toLowerCase().includes(q));
    const key = query.sort || "name";
    const dir = query.dir === "desc" ? -1 : 1;
    rows = rows.slice().sort((a, b) => {
        const av = key === "name" ? (a.name || a.email) : a[key];
        const bv = key === "name" ? (b.name || b.email) : b[key];
        if((av === "") !== (bv === "")){ return av === "" ? 1 : -1; }
        return av.localeCompare(bv) * dir || a.email.localeCompare(b.email);
    });
    const limit = query.limit || 50;
    const page = query.page || 1;
    return { items: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit, hasMore: page * limit < rows.length,
        syncedAt: mock.status.lastSyncedAt, syncing: false };
};
const mockSynced = () => {
    const now = new Date().toISOString();
    mock.status = { ...mock.status, lastSyncedAt: now, sync: { status: "idle", error: null, contactCount: mock.rows.length, lastAttemptAt: now } };
    return mock.status;
};
const notConnected = () => Promise.reject({ message: "Connect your iCloud account first.", reason: "not_connected" });

/* ---------------- API ---------------- */
const BASE = "user/contacts/icloud";

const ICloudContactsApi = {
    status(){
        if(isICloudMockMode()){ return delay(mock.status, 150); }
        return request("GET", BASE+"/status");
    },
    /** Validates with Apple, stores the encrypted app-specific password and runs the first import. Returns status (+ syncError). */
    connect(appleAccountEmail, appSpecificPassword){
        if(isICloudMockMode()){
            if(String(appSpecificPassword).replace(/[\s-]/g, "") === "wrongwrongwrongw"){
                return new Promise((resolve, reject) => setTimeout(() => reject({ message: "Apple didn't accept this Apple Account email and app-specific password. Check the email, or create a new app-specific password and try again.",
                    reason: "invalid_credentials", errors: { app_specific_password: "Apple didn't accept this app-specific password." } }), 600));
            }
            mock.status = { ...mock.status, status: "connected", connected: true, account: { email: String(appleAccountEmail).trim().toLowerCase(), name: "" },
                connectedAt: new Date().toISOString(), addressBooks: 1 };
            mockSynced();
            return delay({ ...mock.status, syncError: null }, 900);
        }
        return request("POST", BASE+"/connect", { data: { apple_account_email: appleAccountEmail, app_specific_password: appSpecificPassword } });
    },
    /** "Sync now". Returns status + { result }. */
    sync(full = false){
        if(isICloudMockMode()){ return mock.status.connected ? delay({ ...mockSynced(), result: { started: true, alreadyRunning: false } }, 700) : notConnected(); }
        return request("POST", BASE+"/sync", { data: { full: !!full } });
    },
    /** query: { q, sort: "name"|"email"|"company", dir: "asc"|"desc", page, limit } */
    contacts(query = {}, source = null){
        if(isICloudMockMode()){ return mock.status.connected ? delay(mockContacts(query), 300) : notConnected(); }
        const params = {};
        Object.keys(query).forEach(k => { if(query[k] !== "" && query[k] !== null && query[k] !== undefined){ params[k] = query[k]; } });
        return request("GET", BASE, { params, source });
    },
    disconnect(){
        if(isICloudMockMode()){
            mock.status = { ...mock.status, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, addressBooks: 0, sync: null };
            return delay(mock.status);
        }
        return request("DELETE", BASE+"/connection");
    }
};

export default ICloudContactsApi;
